import { useTranslation } from 'react-i18next';
import { MoreVertical } from 'lucide-react';
import { cn } from '@/lib/utils';

type Props = {
  label: string;
  active: boolean;
  disabled?: boolean;
  count?: number;
  color?: string;
  onSelect: () => void;
  onManage?: () => void;
};

/**
 * SegmentChip — filter pill for a product segment, implemented as a toggle button.
 * Uses aria-pressed (NOT role="tab"/aria-selected): these pills have no associated
 * tabpanel and no arrow-key roving tabindex, so a tab role would be a false affordance.
 * Used inside a role="group" bar (siehe ProductSegmentBar, S2-T9).
 */
export function SegmentChip({ label, active, disabled, count, color, onSelect, onManage }: Props) {
  const { t } = useTranslation();

  return (
    <div className="inline-flex items-center">
      <button
        type="button"
        data-testid="segment-chip"
        aria-pressed={active}
        aria-disabled={disabled || undefined}
        aria-label={disabled ? `${label} – ${t('productSegments.soon')}` : undefined}
        onClick={() => !disabled && onSelect()}
        className={cn(
          'inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium transition',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-1',
          disabled
            ? 'cursor-not-allowed border border-dashed border-neutral-300 text-neutral-400'
            : active
              ? 'bg-primary-500 text-white'
              : 'border border-neutral-200 bg-white text-neutral-700 hover:bg-neutral-50 dark:bg-neutral-900 dark:text-neutral-300',
        )}
      >
        {color && !disabled && (
          <span
            aria-hidden="true"
            className="h-2 w-2 rounded-full"
            style={{ backgroundColor: color }}
          />
        )}
        {label}
        {typeof count === 'number' && (
          <span className="tabular-nums opacity-70">{count}</span>
        )}
        {disabled && (
          <span
            data-testid="segment-chip-soon"
            className="rounded bg-neutral-100 px-1 text-2xs text-neutral-500"
          >
            {t('productSegments.soon')}
          </span>
        )}
      </button>
      {onManage && (
        <button
          type="button"
          data-testid="segment-chip-manage"
          aria-label={t('productSegments.menu.aria')}
          onClick={onManage}
          className="ml-1 rounded p-1 text-neutral-500 hover:bg-neutral-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-1 dark:hover:bg-neutral-800"
        >
          <MoreVertical className="h-3.5 w-3.5" aria-hidden="true" />
        </button>
      )}
    </div>
  );
}
